import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

type FeatureFlags =
  | "enable-multiple-esc"
  | "portus-enable-reduced-reports"
  | "portus-enable-bulk-report-request"
  | "agency-export"
  | "janus-tra050-zip-import-enabled";

interface PayloadTypes {
  "enable-multiple-esc": never;
  "portus-enable-reduced-reports": never;
  "portus-enable-bulk-report-request": never;
  "agency-export": never;
  "janus-tra050-zip-import-enabled": never;
}

const useFeatureFlag = (feature: FeatureFlags) => {
  const posthog = usePostHog();
  const [isEnabled, setIsEnabled] = useState<boolean | undefined>(() =>
    posthog?.isFeatureEnabled(feature),
  );

  useEffect(() => {
    if (!posthog) {
      setIsEnabled(false);
      return;
    }

    // Set initial value
    const initialValue = posthog.isFeatureEnabled(feature);
    setIsEnabled(initialValue ?? false);

    // Listen for feature flag updates
    const unsubscribe = posthog.onFeatureFlags(() => {
      const value = posthog.isFeatureEnabled(feature);
      setIsEnabled(value ?? false);
    });

    return () => {
      unsubscribe();
    };
  }, [posthog, feature]);

  return isEnabled;
};

const useFeatureFlagPayload = <T extends FeatureFlags>(
  feature: T,
): PayloadTypes[T] | undefined => {
  const posthog = usePostHog();
  return posthog?.getFeatureFlagPayload(feature) as unknown as PayloadTypes[T];
};

export { useFeatureFlag, useFeatureFlagPayload };
